import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';
import { ICP } from '../state/icp/icp.state';

@Injectable({
  providedIn: 'root'
})
export class IcpService {
  private apiUrl = environment.api.url;

  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) {}

  private getHttpOptions(): Observable<{ headers: HttpHeaders }> {
    return this.authService.token().pipe(
      map(token => ({
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        }),
      }))
    );
  }

  // Get all ICPs
  getIcps(params?: {
    page?: number;
    per_page?: number;
    search?: string;
    filter?: { is_default?: boolean };
    include?: string[];
  }): Observable<{
    data: ICP[];
    pagination: {
      total: number;
      per_page: number;
      current_page: number;
      last_page: number;
      from: number;
      to: number;
    };
    message: string;
  }> {
    const url = `${this.apiUrl}/icps`;

    let httpParams = new HttpParams();
    
    if (params?.page) {
      httpParams = httpParams.set('page', params.page.toString());
    }
    if (params?.per_page) {
      httpParams = httpParams.set('per_page', params.per_page.toString());
    }
    if (params?.search) {
      httpParams = httpParams.set('search', params.search);
    }
    if (params?.filter?.is_default !== undefined) {
      httpParams = httpParams.set('filter[is_default]', params.filter.is_default.toString());
    }
    if (params?.include && params.include.length > 0) {
      httpParams = httpParams.set('include', params.include.join(','));
    }

    return this.getHttpOptions().pipe(
      switchMap(options =>
        this.http.get<any>(url, {
          ...options,
          params: httpParams
        }).pipe(
          map((response) => {
            if (!response || !response.data) {
              throw new Error('Failed to load ICPs: Invalid response structure');
            }

            let icpsArray: ICP[];
            let paginationData: any;

            // Handle paginated response structure
            if (response.data.data && Array.isArray(response.data.data)) {
              icpsArray = response.data.data;
              paginationData = response.data;
            } else if (Array.isArray(response.data)) {
              icpsArray = response.data;
              paginationData = null;
            } else {
              throw new Error('Failed to load ICPs: Unexpected response structure');
            }

            return {
              data: icpsArray,
              pagination: paginationData ? {
                total: paginationData.total || icpsArray.length,
                per_page: paginationData.per_page || params?.per_page || 10,
                current_page: paginationData.current_page || params?.page || 1,
                last_page: paginationData.last_page || Math.ceil((icpsArray.length || 0) / (params?.per_page || 10)),
                from: paginationData.from || (icpsArray.length > 0 ? 1 : 0),
                to: paginationData.to || icpsArray.length
              } : {
                total: icpsArray.length,
                per_page: params?.per_page || 10,
                current_page: params?.page || 1,
                last_page: Math.ceil((icpsArray.length || 0) / (params?.per_page || 10)),
                from: icpsArray.length > 0 ? 1 : 0,
                to: icpsArray.length
              },
              message: response.metadata?.message || 'ICPs loaded successfully',
            };
          }),
          catchError((error) => throwError(() => error))
        )
      )
    );
  }

  // Get single ICP detail
  getIcpDetail(id: string): Observable<{ data: ICP; message: string }> {
    const url = `${this.apiUrl}/icps/${id}`;
    
    // Include all relations for detailed view
    const params = new HttpParams()
      .set('include', 'industries,actual_duty,company_size,customer_company_sizes,company_revenues,customer_industries,target_countries,country_exclusions,trend_employee,trend_revenue,close_deal,buying_cycle,workspaces');

    return this.getHttpOptions().pipe(
      switchMap(options =>
        this.http.get<any>(url, { ...options, params }).pipe(
          map((response) => {
            if (!response || !response.data) {
              throw new Error('Failed to load ICP details: Invalid response structure');
            }
            return {
              data: response.data,
              message: response.metadata?.message || 'ICP details loaded successfully',
            };
          }),
          catchError((error) => throwError(() => error))
        )
      )
    );
  }

  // Create new ICP
  createIcp(icpData: Partial<ICP>): Observable<{ data: ICP; message: string }> {
    const url = `${this.apiUrl}/icps`;

    return this.getHttpOptions().pipe(
      switchMap(options =>
        this.http.post<any>(url, icpData, options).pipe(
          map((response) => {
            if (!response.data) {
              throw new Error('Failed to create ICP: ' + (response.metadata?.message || response.message));
            }
            return {
              data: response.data,
              message: response.metadata?.message || response.message,
            };
          }),
          catchError((error) => throwError(() => error))
        )
      )
    );
  }

  // Update existing ICP
  updateIcp(id: number, icpData: Partial<ICP>): Observable<{ data: ICP; message: string }> {
    const url = `${this.apiUrl}/icps/${id}`;

    return this.getHttpOptions().pipe(
      switchMap(options =>
        this.http.put<any>(url, icpData, options).pipe(
          map((response) => {
            if (!response.data) {
              throw new Error('Failed to update ICP: ' + (response.metadata?.message || response.message));
            }
            return {
              data: response.data,
              message: response.metadata?.message || response.message,
            };
          }),
          catchError((error) => throwError(() => error))
        )
      )
    );
  }

  // Delete ICP
  deleteIcp(id: number): Observable<{ message: string }> {
    const url = `${this.apiUrl}/icps/${id}`;

    return this.getHttpOptions().pipe(
      switchMap(options =>
        this.http.delete<any>(url, options).pipe(
          map((response) => {
            return {
              message: response.metadata?.message || response.message,
            };
          }),
          catchError((error) => throwError(() => error))
        )
      )
    );
  }

  // Clone ICP
  cloneIcp(id: number, name?: string): Observable<{ data: ICP; message: string }> {
    const url = `${this.apiUrl}/icps/${id}/clone`;
    const payload: any = {};
    if (name) {
      payload.name = name;
    }

    return this.getHttpOptions().pipe(
      switchMap(options =>
        this.http.post<any>(url, payload, options).pipe(
          map((response) => {
            if (!response.data) {
              throw new Error('Failed to clone ICP: ' + (response.metadata?.message || response.message));
            }
            return {
              data: response.data,
              message: response.metadata?.message || response.message || 'ICP cloned successfully',
            };
          }),
          catchError((error) => throwError(() => error))
        )
      )
    );
  }

  // Link ICP to workspace
  linkIcpToWorkspace(workspaceId: string, icpId: number): Observable<{ message: string }> {
    const url = `${this.apiUrl}/workspaces/${workspaceId}/link-icp`;
    const payload = {
      icp_id: icpId
    };

    return this.getHttpOptions().pipe(
      switchMap(options =>
        this.http.post<any>(url, payload, options).pipe(
          map((response) => {
            return {
              message: response.metadata?.message || response.message || 'ICP linked to workspace successfully',
            };
          }),
          catchError((error) => throwError(() => error))
        )
      )
    );
  }
}